'use client'

import { DEFAULT_PREFERENCES } from '@/lib/constants/preferences-client'
import { updatePreferenceCategoryAction, updateUserPreferenceAction } from '@/lib/services/actions/user-preferences'
import { UserPreferences } from '@/lib/types/user/preferences'
import { showToast } from '@/lib/utils/toast'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCurrentUser } from './use-current-user'

/**
 * Hook to access and update user preferences using nested structure
 *
 * Provides:
 * - Type-safe access to nested preferences
 * - Functions to update preferences by category
 * - Loading state tracking
 */
export function usePreferences() {
  const dispatch = useDispatch()
  const user = useCurrentUser()
  const reduxPreferences = useSelector((state: any) => state.preferences) || {}

  // Merge Redux state with defaults to ensure complete structure
  const preferences: UserPreferences = {
    ui: {
      ...DEFAULT_PREFERENCES.ui,
      ...(reduxPreferences.ui || {})
    },
    notifications: {
      ...(DEFAULT_PREFERENCES.notifications ?? {}),
      ...(reduxPreferences.notifications || {}),
      quietHours: {
        ...(DEFAULT_PREFERENCES.notifications?.quietHours ?? {}),
        ...(reduxPreferences.notifications?.quietHours || {})
      }
    },
    application: {
      ...DEFAULT_PREFERENCES.application,
      ...(reduxPreferences.application || {})
    },
    localization: {
      ...DEFAULT_PREFERENCES.localization,
      ...(reduxPreferences.localization || {})
    }
  }

  /**
   * Update UI preferences
   */
  const updateUIPreferences = useCallback(
    async (updates: Partial<UserPreferences['ui']>) => {
      console.log('[usePreferences] updateUIPreferences called:', {
        updates,
        userId: user?.id,
        isAuthenticated: !!user?.id
      })

      if (!user?.id) {
        // For unauthenticated users, update Redux only
        console.log('[usePreferences] User not authenticated, updating Redux only')
        dispatch({ type: 'preferences/updateUI', payload: updates })
        return { success: true }
      }

      try {
        console.log('[usePreferences] Calling server action for authenticated user')
        const response = await updatePreferenceCategoryAction('ui', updates)

        console.log('[usePreferences] Server response:', response)

        if (!response.success) {
          console.error('[usePreferences] Server action failed:', response.error)
          showToast({ title: `Failed to save UI preferences: ${response.error}`, color: 'danger', timeout: 5000 })
        } else {
          // Update Redux with server response
          if (response.data?.preferences) {
            console.log('[usePreferences] Updating Redux with server data:', response.data.preferences)
            console.log('[usePreferences] Full response data structure:', response.data)
            // The server returns { preferences, privacySettings, version, updatedAt }
            // We need to dispatch the preferences object directly
            dispatch({ type: 'preferences/set', payload: response.data.preferences })
          } else {
            console.warn('[usePreferences] No preferences data in server response:', response.data)
          }
        }

        return response
      } catch (error) {
        console.error('[usePreferences] Error in updateUIPreferences:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast({ title: `Error saving UI preferences: ${errorMessage}`, color: 'danger', timeout: 5000 })
        return { success: false, error: errorMessage }
      }
    },
    [dispatch, user?.id]
  )

  /**
   * Update notification preferences
   */
  const updateNotificationPreferences = useCallback(
    async (updates: Partial<UserPreferences['notifications']>) => {
      if (!user?.id) {
        dispatch({ type: 'preferences/updateNotifications', payload: updates })
        return { success: true }
      }

      try {
        const response = await updatePreferenceCategoryAction('notifications', updates)

        if (!response.success) {
          showToast({
            title: `Failed to save notification preferences: ${response.error}`,
            color: 'danger',
            timeout: 5000
          })
        } else {
          if (response.data?.preferences) {
            console.log('[usePreferences] Updating Redux with notification preferences:', response.data.preferences)
            dispatch({ type: 'preferences/set', payload: response.data.preferences })
          } else {
            console.warn('[usePreferences] No preferences data in notification response:', response.data)
          }
        }

        return response
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast({ title: `Error saving notification preferences: ${errorMessage}`, color: 'danger', timeout: 5000 })
        return { success: false, error: errorMessage }
      }
    },
    [dispatch, user?.id]
  )

  /**
   * Update application preferences
   */
  const updateApplicationPreferences = useCallback(
    async (updates: Partial<UserPreferences['application']>) => {
      if (!user?.id) {
        dispatch({ type: 'preferences/updateApplication', payload: updates })
        return { success: true }
      }

      try {
        const response = await updatePreferenceCategoryAction('application', updates)

        if (!response.success) {
          showToast({
            title: `Failed to save application preferences: ${response.error}`,
            color: 'danger',
            timeout: 5000
          })
        } else {
          if (response.data?.preferences) {
            console.log('[usePreferences] Updating Redux with application preferences:', response.data.preferences)
            dispatch({ type: 'preferences/set', payload: response.data.preferences })
          } else {
            console.warn('[usePreferences] No preferences data in application response:', response.data)
          }
        }

        return response
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast({ title: `Error saving application preferences: ${errorMessage}`, color: 'danger', timeout: 5000 })
        return { success: false, error: errorMessage }
      }
    },
    [dispatch, user?.id]
  )

  /**
   * Update localization preferences
   */
  const updateLocalizationPreferences = useCallback(
    async (updates: Partial<UserPreferences['localization']>) => {
      if (!user?.id) {
        dispatch({ type: 'preferences/updateLocalization', payload: updates })
        return { success: true }
      }

      try {
        const response = await updatePreferenceCategoryAction('localization', updates)

        if (!response.success) {
          showToast({
            title: `Failed to save localization preferences: ${response.error}`,
            color: 'danger',
            timeout: 5000
          })
        } else {
          if (response.data?.preferences) {
            console.log('[usePreferences] Updating Redux with localization preferences:', response.data.preferences)
            dispatch({ type: 'preferences/set', payload: response.data.preferences })
          } else {
            console.warn('[usePreferences] No preferences data in localization response:', response.data)
          }
        }

        return response
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast({ title: `Error saving localization preferences: ${errorMessage}`, color: 'danger', timeout: 5000 })
        return { success: false, error: errorMessage }
      }
    },
    [dispatch, user?.id]
  )

  /**
   * Update multiple preference categories at once
   */
  const updatePreferences = useCallback(
    async (updates: Partial<UserPreferences>) => {
      if (!user?.id) {
        dispatch({ type: 'preferences/update', payload: updates })
        return { success: true }
      }

      try {
        const response = await updateUserPreferenceAction({ preferences: updates })

        if (!response.success) {
          showToast({ title: `Failed to save preferences: ${response.error}`, color: 'danger', timeout: 5000 })
        } else {
          if (response.data?.preferences) {
            console.log('[usePreferences] Updating Redux with multiple preferences:', response.data.preferences)
            dispatch({ type: 'preferences/set', payload: response.data.preferences })
          } else {
            console.warn('[usePreferences] No preferences data in multiple update response:', response.data)
          }
        }

        return response
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast({ title: `Error saving preferences: ${errorMessage}`, color: 'danger', timeout: 5000 })
        return { success: false, error: errorMessage }
      }
    },
    [dispatch, user?.id]
  )

  return {
    preferences,
    updateUIPreferences,
    updateNotificationPreferences,
    updateApplicationPreferences,
    updateLocalizationPreferences,
    updatePreferences,
    isAuthenticated: !!user?.id
  }
}
